import { insertartBitacoraCliente } from "../clientes/thunkClientes";
import { insertartBitacoraEmpresa } from "../Empresa/thunkEmpresa";
import { crearContratonApi, deleteEliminarContrato, obtener_NumeroConsecutivoContrato, obtenerContrato, obtenerLista_Contratos } from "./Api_Contratos_Providers";

/**
 * Obtiene la lista de contratos llamando a la API correspondiente.
 * 
 * @param {Array} estados - Lista opcional de estados de los contratos (actualmente no utilizada en la función).
 * @returns {Promise<Array>} Promesa que resuelve con la lista de contratos obtenidos de la API.
 */
export const obtenerListaContratos = (estados) => {
    return async () => {
        try {
            // Llama a la función para obtener la lista de contratos desde la API, pasando los estados si es necesario
            const resultado = await obtenerLista_Contratos({ estados });

            // Devuelve los datos de la respuesta, asumiendo que están dentro de la propiedad 'data.data'.
            return resultado.data.data;
        } catch (error) {
            // Maneja cualquier error que ocurra durante la llamada a la API
            console.error("Error al obtener la lista de Contratos:", error);
        }
    };
};

/**
 * Extrae el contrato asociado a un conjunto de identificadores.
 * 
 * @param {Object} ids - Objeto que contiene los identificadores de los contratos a recuperar.
 * @returns {Promise<Object>} Promesa que resuelve con los detalles del contrato solicitado.
 */
export const extraerContratoId = (ids) => {
    return async () => {
        try {
            // Llama a la API para obtener el contrato utilizando los identificadores proporcionados
            const resultado = await obtenerContrato({ ids });

            // Devuelve los datos del contrato recuperado, asumidos dentro de 'data.data'.
            return resultado.data;
        } catch (error) {
            // Maneja los errores que ocurran durante la solicitud
            console.error("Error al obtener Contrato:", error);
        }
    };
};

/**
 * Obtiene el número consecutivo del contrato a través de la API.
 * 
 * @returns {Promise<Object>} Promesa que resuelve con el primer elemento de la respuesta de la API,
 * que contiene el número consecutivo del contrato.
 */
export const obtenerNumeroConsecutivoContrato = () => {
    return async () => {
        try {
            // Realiza la llamada para obtener el número consecutivo de contrato
            const respuesta = await obtener_NumeroConsecutivoContrato();

            // Devuelve el primer dato del array de datos, que es el número consecutivo del contrato
            return respuesta.data.data[0];
        } catch (error) {
            // Manejo de errores si la solicitud para obtener el número consecutivo falla
            console.error("Error al obtener el número consecutivo:", error);
            throw new Error("No se pudo obtener el número consecutivo");
        }
    };
};

/**
 * Acción para crear un contrato utilizando los datos del formulario y las fechas proporcionadas.
 * Además, registra la creación del contrato en las bitácoras correspondientes.
 * 
 * @param {Object} formulario - Datos del formulario para crear el contrato.
 * @param {Object} fechas - Fechas relevantes para el contrato.
 * @param {boolean} mostrarCalendario - Indica si se debe mostrar el calendario en el contrato.
 * @returns {Promise<Object>} Promesa que resuelve con los datos del contrato creado.
 */
export const crearContrato = (formulario, fechas, mostrarCalendario) => {
    return async (dispatch, getState) => {
        const { id_admin } = getState().auth;
        try {
            // Combina los datos del formulario y las fechas en un solo objeto para enviarlo a la API
            const datosCompletos = {
                formulario, // Datos del formulario del contrato
                fechas, // Fechas del contrato
                id_admin, // ID del administrador que está creando el contrato
                mostrarCalendario, // Bandera para mostrar el calendario en el contrato
            };

            // Llama a la API para crear el contrato con los datos combinados
            const respuesta = await crearContratonApi({ datosCompletos });

            // Si el cliente tiene un ID válido, registra la creación en la bitácora de clientes
            if (formulario.datosCliente.id_cliente > 0) {
                await dispatch(
                    insertartBitacoraCliente({
                        bitacora_cliente: formulario.datosCliente.id_cliente,
                        bitacora_nombreCliente: "Contrato creada",
                        bitacora_detalle: `Se le ha generado un Contrato para este cliente Contrato #${formulario.consecutivo} , Nombre Contrato: ${formulario.nombreEvento}`,
                    }),
                );
            }

            // Si la empresa tiene un ID válido, registra la creación en la bitácora de empresas
            if (formulario.empresaSeleccionada > 0) {
                const nota = `Se le ha generado un Contrato para esta empresa Contrato #${formulario.consecutivo} , Nombre Contrato: ${formulario.nombreEvento}`;
                const idEmpresa = formulario.empresaSeleccionada;
                await dispatch(insertartBitacoraEmpresa(nota, idEmpresa));
            }

            // Devuelve los datos del contrato recién creado
            const { data } = respuesta;
            return data;
        } catch (error) {
            // Maneja cualquier error durante el proceso de creación del contrato
            console.error("Error al crear la Contrato:", error.message || error);
            throw new Error("No se pudo crear la Contrato. Verifique los datos proporcionados.");
        }
    };
};

/**
 * Acción para editar un contrato, eliminando el contrato previo y creando uno nuevo.
 * 
 * @param {Object} formulario - Datos del formulario para editar el contrato.
 * @param {Object} fechas - Fechas relevantes para el contrato editado.
 * @param {boolean} mostrarCalendario - Indica si se debe mostrar el calendario en el contrato editado.
 * @param {string} id_admin - ID del administrador que realiza la edición.
 * @param {Array} ids - Identificadores del contrato a eliminar.
 * @returns {Promise<Object>} Promesa que resuelve con los datos del contrato editado.
 */
export const editarContrato = (formulario, fechas, mostrarCalendario, id_admin, ids) => {
    return async (dispatch) => {
        try {
            // Combina los datos del formulario y las fechas en un solo objeto
            const datosCompletos = {
                formulario, // Datos del formulario para el nuevo contrato
                fechas, // Fechas del contrato
                id_admin, // ID del administrador que realiza la edición
                mostrarCalendario, // Bandera para mostrar el calendario en el contrato
            };

            // Elimina el contrato previo utilizando los identificadores proporcionados
            await dispatch(eliminarContrato(ids));

            // Crea un nuevo contrato utilizando los datos combinados
            const respuesta2 = await crearContratonApi({ datosCompletos });

            // Extrae y devuelve los datos del contrato creado
            const { data } = respuesta2;
            return data;
        } catch (error) {
            // Maneja errores que ocurren durante la edición del contrato
            console.error("Error al editar la Contrato:", error.message || error);
            throw new Error("No se pudo editar la Contrato. Verifique los datos proporcionados.");
        }
    };
};

/**
 * Acción para eliminar un contrato específico a través de la API.
 * 
 * @param {string} id - Identificador único del contrato a eliminar.
 * @returns {Promise<Object>} Promesa que resuelve con la respuesta de la eliminación del contrato.
 */
export const eliminarContrato = (id) => {
    return async () => {
        try {
            // Realiza la solicitud para eliminar el contrato con el ID proporcionado
            const respuesta = await deleteEliminarContrato({ id });

            // Devuelve la respuesta de la API tras la eliminación
            return respuesta.data;
        } catch (error) {
            // Maneja cualquier error durante la solicitud para eliminar el contrato
            console.error("Error al eliminar la Contrato:", error);
            throw new Error("No se pudo eliminar la Contrato");
        }
    };
};
